import React from 'react';
import PropTypes from 'prop-types';
//import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import TableHead from '@material-ui/core/TableHead';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import _ from 'lodash';

import { PureRecordRow } from '../../libs/tableUtilities';

function renderLabel(label) {
	if (_.isArray(label)) {
		return (
			<div>
				<div style={{ lineHeight: '1.2' }}>{label[0]}</div>
				<div style={{ fontSize: '90%', color: '#444', fontWeight: 400, lineHeight: 1 }}>
					{label[1]}
				</div>
			</div>
		);
	} else {
		return label;
	}
}

function EnhancedTableHead(props) {
	const { columns, classes, order, orderBy, onRequestSort, makeSticky } = props;
	const createSortHandler = (property) => (event) => {
		onRequestSort(event, property);
	};

	return (
		<TableHead>
			<TableRow>
				{columns.map((col) => {
					const style = makeSticky
						? {
								zIndex: 1,
								position: 'sticky',
								top: 0,
								backgroundColor: '#fff',
						  }
						: {};

					return (
						<TableCell
							key={col.sortId || col.displayId}
							align={col.numeric ? 'right' : 'left'}
							padding={col.padding ? col.padding : 'default'}
							sortDirection={orderBy === col.sortId ? order : false}
							style={style}
						>
							{col.noSort && <span style={{ whiteSpace: 'nowrap' }}>{renderLabel(col.label)}</span>}

							{!col.noSort && (
								<TableSortLabel
									active={orderBy === col.sortId}
									direction={order}
									onClick={createSortHandler(col.sortId)}
								>
									{renderLabel(col.label)}
									{orderBy === col.sortId ? (
										<span className={classes.visuallyHidden}>
											{order === 'desc' ? 'sorted descending' : 'sorted ascending'}
										</span>
									) : null}
								</TableSortLabel>
							)}
						</TableCell>
					);
				})}
			</TableRow>
		</TableHead>
	);
}

EnhancedTableHead.propTypes = {
	columns: PropTypes.array.isRequired,
	classes: PropTypes.object.isRequired,
	onRequestSort: PropTypes.func.isRequired,
	order: PropTypes.oneOf(['asc', 'desc']).isRequired,
	orderBy: PropTypes.string.isRequired,
};

function desc(a, b, orderBy) {
	if (b[orderBy] < a[orderBy]) {
		return -1;
	}
	if (b[orderBy] > a[orderBy]) {
		return 1;
	}
	return 0;
}

function stableSort(array, cmp) {
	const stabilizedThis = array.map((el, index) => [el, index]);
	stabilizedThis.sort((a, b) => {
		const order = cmp(a[0], b[0]);
		if (order !== 0) return order;
		return a[1] - b[1];
	});
	return stabilizedThis.map((el) => el[0]);
}

function getSorting(order, orderBy) {
	return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

const useStyles = makeStyles(() => ({
	table: {
		//minWidth: 750,
	},
	visuallyHidden: {
		border: 0,
		clip: 'rect(0 0 0 0)',
		height: 1,
		margin: -1,
		overflow: 'hidden',
		padding: 0,
		position: 'absolute',
		top: 20,
		width: 1,
	},
	oddRow: {
		background: '#f9f9f9',
	},
	totalRow: {
		padding: '5px 10px',
		borderBottom: '1px solid #bbb',
		verticalAlign: 'top',
		backgroundColor: '#eaf0f7',
		color: '#444',
		textAlign: 'right',
		fontWeight: '500',
		lineHeight: 1.7,
		borderTop: '2px solid #888',
		paddingTop: 10,
	},
}));

export default function EnhancedTable(props) {
	const {
		rows,
		totalRows,
		columns,
		refreshId,
		defaultOrder,
		defaultOrderBy,
		isDense = false,
		makeSticky = false,
	} = props;

	const classes = useStyles();
	const [order, setOrder] = React.useState(defaultOrder || 'asc');
	const [orderBy, setOrderBy] = React.useState(defaultOrderBy || columns[0].sortId);

	const handleRequestSort = (event, property) => {
		const isDesc = orderBy === property && order === 'desc';
		setOrder(isDesc ? 'asc' : 'desc');
		setOrderBy(property);
	};

	return (
		<TableContainer style={makeSticky ? { overflow: 'initial' } : {}}>
			<Table
				className={classes.table}
				size={isDense ? 'small' : 'medium'}
				aria-label="enhanced table"
			>
				<EnhancedTableHead
					classes={classes}
					order={order}
					orderBy={orderBy}
					onRequestSort={handleRequestSort}
					columns={columns}
					makeSticky={makeSticky}
				/>
				<TableBody>
					{stableSort(rows, getSorting(order, orderBy)).map((row, i) => {
						const rowClass = i % 2 ? classes.oddRow : classes.even;

						return (
							<PureRecordRow
								hover
								role="checkbox"
								tabIndex={-1}
								key={row.key}
								refreshId={refreshId}
								className={rowClass}
							>
								{columns.map(({ displayId, padding, useClassName, useStyle }) => {
									let rowClassName;
									let style = {};
									if (useClassName) {
										rowClassName = row[useClassName];
									}
									if (useStyle) {
										style = row[useStyle];
									}
									return (
										<TableCell
											key={displayId}
											padding={padding ? padding : null}
											className={rowClassName}
											style={style}
										>
											{row[displayId]}
										</TableCell>
									);
								})}
							</PureRecordRow>
						);
					})}

					{!!totalRows &&
						totalRows.map((row, i) => {
							return (
								<PureRecordRow
									hover
									tabIndex={-1}
									key={row.key}
									refreshId={refreshId}
									className={classes.totalRow}
								>
									{columns.map(({ displayId, padding, useStyle }) => {
										let style = {};
										if (useStyle) {
											style = row[useStyle] || {};
										}
										style.paddingTop = 10;
										style.paddingBottom = 10;
										style.fontSize = '100%';
										return (
											<TableCell key={displayId} padding={padding ? padding : null} style={style}>
												<strong>{row[displayId]}</strong>
											</TableCell>
										);
									})}
								</PureRecordRow>
							);
						})}
				</TableBody>
			</Table>
		</TableContainer>
	);
}

EnhancedTable.propTypes = {
	rows: PropTypes.array.isRequired,
	columns: PropTypes.array.isRequired,
	defaultOrder: PropTypes.string,
	defaultOrderBy: PropTypes.string,
	refreshId: PropTypes.number,
};
